import {
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  Vector3,
} from "three";
import Experience from "./Experience.js";

export default class Baked {
  constructor() {
    this.experience = new Experience();
    this.resources = this.experience.resources;
    this.scene = this.experience.scene;
    this.renderer = this.experience.renderer.instance;
    this.maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.setModels();
  }

  setMaterial = (object, material) => {
    object.traverse((child) => {
      if (child.isMesh) {
        child.material = material;
      }
    });
  };

  configureTexture = (texture) => {
    texture.anisotropy = this.maxAnisotropy;
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  };

  setModels = () => {
    this.model = {};
    this.model.room1 = this.resources.items._roomModel.scene;

    this.bakedTexture1 = this.configureTexture(this.resources.items.baked1);
    this.model.material = new MeshBasicMaterial({
      map: this.bakedTexture1,
    });

    this.model.room2 = this.resources.items._roomModel2.scene;
    this.bakedTexture2 = this.configureTexture(this.resources.items.baked2);

    this.model.material2 = new MeshBasicMaterial({
      map: this.bakedTexture2,
    });

    this.model.room3 = this.resources.items._roomModel3.scene;
    this.bakedTexture3 = this.configureTexture(this.resources.items.baked3);

    this.model.material3 = new MeshBasicMaterial({
      map: this.bakedTexture3,
    });

    this.model.linkedin = this.resources.items.linkedin.scene;
    this.model.linkedin.name = "linkedin";
    this.model.github = this.resources.items.github.scene;
    this.model.github.name = "github";
    this.model.itchio = this.resources.items.itchio.scene;
    this.model.itchio.name = "itchio";

    this.setMaterial(this.model.room1, this.model.material);
    this.setMaterial(this.model.room2, this.model.material2);
    this.setMaterial(this.model.room3, this.model.material3);
    this.setMaterial(this.model.linkedin, this.model.material3);
    this.setMaterial(this.model.github, this.model.material3);

    // The "itchio" slot now holds the Instagram logo (links to @mr.nsm.official),
    // styled as a wooden picture-frame plaque to match the LinkedIn / GitHub
    // plaques on the same wall. Six named submeshes get their own unlit
    // MeshBasicMaterials (the baked atlas doesn't apply here):
    //   instagram_frame        → dark walnut wood
    //   instagram_panel        → white card behind the logo
    //   instagram_body         → Instagram pink (camera body)
    //   instagram_ring         → white (lens ring)
    //   instagram_lens_center  → dark (lens pupil)
    //   instagram_flash        → white (flash dot)
    this.model.instagramFrame = new MeshBasicMaterial({ color: 0x4a3526 });
    this.model.instagramPanel = new MeshBasicMaterial({ color: 0xffffff });
    this.model.instagramBody = new MeshBasicMaterial({ color: 0xe4405f });
    this.model.instagramAccent = new MeshBasicMaterial({ color: 0xffffff });
    this.model.instagramLensCenter = new MeshBasicMaterial({ color: 0x2b1a22 });
    this.model.itchio.traverse((child) => {
      if (!child.isMesh) return;
      const n = child.name || "";
      if (n.indexOf("frame") !== -1) {
        child.material = this.model.instagramFrame;
      } else if (n.indexOf("panel") !== -1) {
        child.material = this.model.instagramPanel;
      } else if (n.indexOf("body") !== -1) {
        child.material = this.model.instagramBody;
      } else if (n.indexOf("lens_center") !== -1) {
        child.material = this.model.instagramLensCenter;
      } else {
        child.material = this.model.instagramAccent;
      }
    });

    this.scene.add(this.model.room1);
    this.scene.add(this.model.room2);
    this.scene.add(this.model.room3);

    this.scene.add(this.model.linkedin);
    this.scene.add(this.model.github);
    this.scene.add(this.model.itchio);

    // ----- Sahil clapboard overlay -----
    // The room3 mesh "Claqueta.002" has the original author's name baked into
    // the diffuse atlas (baked3.ktx2). Since the atlas can't be patched in
    // place without re-baking from Blender, we hide that mesh and place a
    // fresh PlaneGeometry just in front of it, textured with a custom
    // Sahil-branded clapboard (loaded via the "sahilClapboard" asset).
    let claqueta = null;
    this.model.room3.traverse((child) => {
      if (
        child.isMesh &&
        child.name &&
        child.name.toLowerCase().indexOf("claqueta") !== -1
      ) {
        claqueta = child;
      }
    });

    if (claqueta) {
      // Hide the original clapboard mesh
      claqueta.visible = false;

      // Worldspace position of the original (room3 is at identity in scene)
      const worldPos = new Vector3();
      claqueta.getWorldPosition(worldPos);

      const clapboardTex = this.configureTexture(
        this.resources.items.sahilClapboard
      );
      const clapboardMat = new MeshBasicMaterial({ map: clapboardTex });
      const clapboardGeom = new PlaneGeometry(0.62, 0.55);
      const clapboardMesh = new Mesh(clapboardGeom, clapboardMat);
      clapboardMesh.name = "sahilClapboard";
      clapboardMesh.position.copy(worldPos);
      // Nudge slightly toward the camera-side so it doesn't z-fight with the
      // hidden original mesh (the clapboard face points roughly +Z).
      clapboardMesh.position.z += 0.04;
      this.scene.add(clapboardMesh);
    }
  };
}
